import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { useSEO } from '@/hooks/useSEO';
import { articles, categories } from '@/mocks/site';
import { Link, useParams } from 'react-router-dom';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article
    ? categories.find((c) => c.id === article.category)
    : undefined;

  const relatedArticles = article
    ? articles
        .filter((a) => a.id !== article.id && a.category === article.category)
        .slice(0, 3)
    : [];

  const articleSeo = article
    ? {
        title: `${article.title} - 黑料吃瓜网`,
        description: article.excerpt,
        keywords: article.tags.join(',') + ',黑料吃瓜,爆料',
        canonical: `https://www.heiliaochigua.com/article/${slug}`,
        ogType: 'article',
        ogImage: article.image,
        articlePublishedTime: article.date,
        articleModifiedTime: article.date,
      }
    : {
        title: '文章 - 黑料吃瓜网',
        description: '',
        canonical: `https://www.heiliaochigua.com/article/${slug}`,
      };

  useSEO(articleSeo);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: article.author,
          url: articleSeo.canonical,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: article.title,
          description: article.excerpt,
          url: articleSeo.canonical,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.heiliaochigua.com' },
              { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://www.heiliaochigua.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: articleSeo.canonical },
            ],
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: 'https://www.heiliaochigua.com' },
            { name: category?.name || '分类', url: `https://www.heiliaochigua.com/category/${article.category}` },
            { name: article.title, url: articleSeo.canonical },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article>
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
                {article.isHot && (
                  <span className="px-3 py-1 text-xs font-bold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                    HOT
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-message-3-line"></i>
                  {article.comments.toLocaleString()} 评论
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 黑料吃瓜网`}
                className="w-full h-auto"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200/70">
              <h2 className="text-lg font-bold font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        title={`${rel.title} - 黑料吃瓜网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-foreground-900 text-xs md:text-sm leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-foreground-400 text-xs mt-1">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}